import React, { Component } from 'react';
import { Button, Container, Grid, Header, Icon, Input, Table } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { Push, push } from 'connected-react-router';
import { EEhsStatus, EEhsType, ehsStatusMap, EUserLevels, IBuilding, IEhs, IUser } from '@relab/alstom-ptw-shared';
import moment from 'moment';

import { ehsUpdateAction, projectAction } from '../../redux/actions';
import { appState, appStateServerRequests } from '../../redux/selectors';
import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { client } from '../../redux/store';
import { TranslationComponent } from '../TranslationComponent';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';
import { EhsDetailPdfExporter } from './EhsDetailPdfExporter';
import { EhsDeleteModal } from './EhsDeleteModal';

interface IEhsPrettied {
  _ehs: IEhs;
  sequentialNumber: string;
  building: string;
  name: string;
  description: string;
  status: string;
  startDate: string;
  startHour: string;
  endHour: string;
  ehsType: string;
}

interface EhssListComponentProps {
  i18n: typeof i18n;
  t: typeof i18n.t;
  ehss: IEhs[];
  projectAction: typeof projectAction;
  user: IUser;
  push: Push;
  buildings: IBuilding[];
  ehsUpdateAction: typeof ehsUpdateAction;
  updateEhsServerRequest?: ServerRequestStatus;
}

interface EhssListComponentState {
  column: string;
  prettiedEhss: IEhsPrettied[];
  activeFilters: {
    sequentialNumber: string;
    building: string;
    name: string;
    status: string;
    ehsType: EEhsType | null;
  };
  direction: 1 | -1;
  requestError?: string;
}

class PEhssListComponent extends Component<EhssListComponentProps, EhssListComponentState> {
  constructor(props: EhssListComponentProps) {
    super(props);

    this.state = {
      column: 'sequentialNumber',
      prettiedEhss: [],
      activeFilters: {
        sequentialNumber: '',
        building: '',
        name: '',
        status: '',
        ehsType: null,
      },
      direction: 1,
    };
  }

  componentDidMount() {
    const activeFiltersString = localStorage.getItem('ehsListFilters');
    if (activeFiltersString) {
      const parsedActiveFilters = JSON.parse(activeFiltersString);

      if (Object.keys(parsedActiveFilters).length === 5) {
        this.setState({
          activeFilters: parsedActiveFilters,
        });
      } else {
        localStorage.setItem('ehsListFilters', null);
      }
    }
  }

  componentDidUpdate(
    prevProps: Readonly<EhssListComponentProps>,
    prevState: Readonly<EhssListComponentState>,
    snapshot?: any
  ) {
    if (this.props.ehss !== prevProps.ehss) {
      this.setState({
        prettiedEhss: this.prettiedEhss(this.props.ehss),
      });
    }
    if (this.props.updateEhsServerRequest?.error !== prevProps.updateEhsServerRequest?.error) {
      this.setState({ requestError: this.props.updateEhsServerRequest.error });
    }
  }

  componentWillUnmount() {
    localStorage.setItem('ehsListFilters', JSON.stringify(this.state.activeFilters));
  }

  prettyBuildingName(buildingId: string): string {
    const building = (this.props.buildings || []).find((building) => building._id === buildingId);
    return building ? building.name : '';
  }

  prettiedEhss = (ehss: IEhs[]): IEhsPrettied[] => {
    return ehss.map((ehs) => ({
      _ehs: ehs,
      sequentialNumber: `#${ehs.sequentialNumber}`,
      building: this.prettyBuildingName(ehs.building),
      name: ehs.name,
      description: ehs.description,
      startDate: moment(ehs.startDate).format('DD.MM.YYYY'),
      startHour: moment(ehs.startHour).format('HH:mm'),
      endHour: moment(ehs.endHour).format('HH:mm'),
      status: ehsStatusMap[ehs.status].name,
      ehsType: ehs.ehsType === EEhsType.MAINTENANCE ? 'MAINTENANCE' : 'SITO',
    }));
  };

  handleFilter = (filterColumn: string, filterValue: string) => {
    const { activeFilters } = this.state;

    const newFilters = {
      ...activeFilters,
      [filterColumn]: filterValue,
    };

    this.setState({
      activeFilters: newFilters,
    });
  };

  handleSort = (clickedColumn: string) => () => {
    const { column, direction } = this.state;

    if (column !== clickedColumn) {
      this.setState({
        column: clickedColumn,
        direction: 1,
      });
    } else {
      this.setState({
        direction: direction === 1 ? -1 : 1,
      });
    }
  };

  applyFilterAndSorting = (ehss: IEhsPrettied[]): IEhsPrettied[] => {
    const { column, direction, activeFilters } = this.state;

    return ehss
      .filter((ehs) => {
        const { name, building, sequentialNumber, status, ehsType } = activeFilters;
        return (
          ehs.name.toUpperCase().includes(name.toUpperCase()) &&
          ehs.building.toUpperCase().includes(building.toUpperCase()) &&
          ehs.sequentialNumber.toUpperCase().includes(sequentialNumber.toUpperCase()) &&
          ehs.status.toUpperCase().includes(status.toUpperCase()) &&
          (ehsType ? ehs.ehsType.toString().toUpperCase().includes(ehsType.toString().toUpperCase()) : true)
        );
      })
      .map((ehs) => {
        ehs.description = ehs.description.split('"').join('');
        return ehs;
      })
      .sort((a: any, b: any) => (a._ehs[column] > b._ehs[column] ? -direction : direction));
  };

  handleSelectEhs = (ehsId: string) => {
    this.props.projectAction(ehsId);
    this.props.push(`/ehss/${ehsId}`);
  };

  handleDownload = (ehsId: string) => {
    client.get(`/ehs/${ehsId}/print`).then((response) => {
      const byteCharacters = atob(response.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    });
  };

  isDeletable = (ehs: IEhs, user: IUser) =>
    user.userLevel === EUserLevels.Admin ||
    (ehs.status === EEhsStatus.NUOVO && (ehs.issuer as IUser)?._id === user._id);

  handleDeleteEhs = (ehsId: string) => {
    this.props.ehsUpdateAction({ _id: ehsId, status: EEhsStatus.CANCELLATO });
  };

  render() {
    const { column, direction, activeFilters, prettiedEhss } = this.state;
    const { t } = this.props;

    const filteredAndSortedEhss = this.applyFilterAndSorting(prettiedEhss);

    return (
      <Container>
        <Grid columns="equal">
          <Grid.Column>
            <Header as="h2">
              <TranslationComponent
                stringKey={'Ehs list ({{length}})'}
                values={{ length: filteredAndSortedEhss.length }}
              />
            </Header>
          </Grid.Column>
          <Grid.Column textAlign="right">
            {/*<CsvDownloader*/}
            {/*  filename="ehss-export"*/}
            {/*  separator={';'}*/}
            {/*  wrapColumnChar={'"'}*/}
            {/*  columns={csvColumns}*/}
            {/*  datas={sortedEhss as Array<{ [key: string]: any }>}*/}
            {/*  suffix={''}*/}
            {/*  prefix={''}*/}
            {/*>*/}
            {/*  <Button>Export</Button>*/}
            {/*</CsvDownloader>*/}
          </Grid.Column>
        </Grid>

        <Grid columns="equal">
          <Grid.Row>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Number')}
                value={activeFilters.sequentialNumber}
                onChange={(e) => {
                  this.handleFilter('sequentialNumber', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Building')}
                value={activeFilters.building}
                onChange={(e) => {
                  this.handleFilter('building', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Name')}
                value={activeFilters.name}
                onChange={(e) => {
                  this.handleFilter('name', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('Status')}
                value={activeFilters.status || ''}
                onChange={(e) => {
                  this.handleFilter('status', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder={t('Enter the filter.')}
                label={t('EHS Type')}
                value={activeFilters.ehsType ?? ''}
                onChange={(e) => {
                  this.handleFilter('ehsType', e.target.value);
                }}
                icon="search"
                style={{ width: '100%' }}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Table sortable celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={column === 'number' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('number')}
              >
                {<TranslationComponent stringKey={'Sequential number'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'building' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('building')}
              >
                {<TranslationComponent stringKey={'Building'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'name' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('name')}
              >
                {<TranslationComponent stringKey={'Name'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'startDate' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('startDate')}
              >
                {<TranslationComponent stringKey={'Date'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'startHour' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('startHour')}
              >
                {<TranslationComponent stringKey={'Start time'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'endHour' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('endHour')}
              >
                {<TranslationComponent stringKey={'End time'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'state' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('state')}
              >
                {<TranslationComponent stringKey={'Status'} />}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === 'ehsType' ? (direction > 0 ? 'ascending' : 'descending') : null}
                onClick={this.handleSort('ehsType')}
              >
                {<TranslationComponent stringKey={'EHS Type'} />}
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredAndSortedEhss.map((ehs) => (
              <Table.Row key={ehs._ehs._id}>
                <Table.Cell>{ehs.sequentialNumber}</Table.Cell>
                <Table.Cell>{ehs.building}</Table.Cell>
                <Table.Cell>{ehs.name}</Table.Cell>
                <Table.Cell>{ehs.startDate}</Table.Cell>
                <Table.Cell>{ehs.startHour}</Table.Cell>
                <Table.Cell>{ehs.endHour}</Table.Cell>
                <Table.Cell>{ehs.status}</Table.Cell>
                <Table.Cell>{ehs.ehsType}</Table.Cell>
                <Table.Cell textAlign="right">
                  <EhsDetailPdfExporter
                    buildings={this.props.buildings}
                    ehs={this.props.ehss.find((dirtyEhs) => dirtyEhs._id === ehs._ehs._id)}
                    userRoles={this.props.user.userRoles}
                  />

                  <Button
                    icon
                    onClick={(event: any) => {
                      event.stopPropagation();
                      this.props.push({
                        pathname: `/ehss/view/${ehs._ehs._id}`,
                      });
                    }}
                  >
                    <Icon name="pencil alternate" />
                  </Button>

                  {this.isDeletable(ehs._ehs, this.props.user) && (
                    <EhsDeleteModal
                      onDelete={() => this.handleDeleteEhs(ehs._ehs._id)}
                      error={this.state.requestError}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Container>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: appState(state).user,
  updateEhsServerRequest: appStateServerRequests(state, ActionType.EHS_UPDATE),
});

export const EhssListComponent = connect(mapStateToProps, { push, projectAction, ehsUpdateAction })(
  withTranslation()(PEhssListComponent)
);
